import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
  Divider,
  TextField,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import billingService, { 
  Bill, 
  BillStatus,
  BillingPeriod 
} from '../../services/newBillingService';

interface BillsViewProps {
  bills: Bill[];
  billingPeriod: BillingPeriod;
  onRefresh: () => void;
}

const BillsView: React.FC<BillsViewProps> = ({
  bills,
  billingPeriod,
  onRefresh
}) => {
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [emailBill, setEmailBill] = useState<Bill | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatHours = (hours: number): string => {
    return `${hours.toFixed(2)}h`;
  };

  const getStatusColor = (status: BillStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case BillStatus.Draft: return 'default';
      case BillStatus.Generated: return 'info';
      case BillStatus.Reviewed: return 'warning';
      case BillStatus.Approved: return 'success';
      case BillStatus.Paid: return 'primary';
      default: return 'default';
    }
  };

  const getStatusText = (status: BillStatus): string => {
    switch (status) {
      case BillStatus.Draft: return 'Draft';
      case BillStatus.Generated: return 'Generated';
      case BillStatus.Reviewed: return 'Reviewed';
      case BillStatus.Approved: return 'Approved';
      case BillStatus.Paid: return 'Paid';
      default: return 'Unknown';
    }
  };

  const toggleRowExpansion = (billId: number) => {
    setExpandedRows(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    );
  };

  const handleViewBill = (bill: Bill) => {
    setSelectedBill(bill);
  };

  const handleCloseBillDialog = () => {
    setSelectedBill(null);
  };

  const handleDownloadBillPdf = async (bill: Bill) => {
    try {
      const pdfBlob = await billingService.billGeneration.downloadBillPdf(bill.id);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bill-${bill.billNumber}-${bill.staffName.replace(/\s+/g, '-')}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading bill PDF:', error);
      alert('Failed to download bill PDF: ' + error.message);
    }
  };

  const handleOpenEmailDialog = (bill: Bill) => {
    setEmailBill(bill);
    setEmailAddress('');
    setEmailError(null);
  };

  const handleSendEmail = async () => {
    if (!emailBill) return;
    if (!emailAddress || !/\S+@\S+\.\S+/.test(emailAddress)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    setEmailError(null);
    try {
      await billingService.billGeneration.sendBillEmail(emailBill.id, emailAddress);
      setEmailBill(null);
      setSnackMessage(`Invoice emailed to ${emailAddress}`);
    } catch (err: any) {
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  if (bills.length === 0) {
    return (
      <Box>
        <Alert severity="info">
          No bills have been generated for this billing period yet.
        </Alert>
      </Box>
    );
  }

  const totalBills = bills.length;
  const totalGrossAmount = bills.reduce((sum, bill) => sum + bill.grossAmount, 0);
  const totalNetAmount = bills.reduce((sum, bill) => sum + bill.netAmount, 0);
  const totalHours = bills.reduce((sum, bill) => sum + bill.totalHours, 0);

  return (
    <Box>
      {/* Summary */}
      <Box mb={3} display="flex" gap={4} flexWrap="wrap">
        <Box>
          <Typography variant="h6">
            Total Bills: {totalBills}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            Total Hours: {formatHours(totalHours)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            Gross Amount: {formatCurrency(totalGrossAmount)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            Net Amount: {formatCurrency(totalNetAmount)}
          </Typography>
        </Box>
      </Box>

      {/* Bills Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Bill Number</TableCell>
              <TableCell>Staff</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Gross Amount</TableCell>
              <TableCell>Net Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Generated</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.map((bill) => (
              <React.Fragment key={bill.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(bill.id)}
                    >
                      {expandedRows.includes(bill.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {bill.billNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {bill.staffName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {bill.employeeId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatHours(bill.totalHours)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Reg: {formatHours(bill.totalRegularHours)}
                        {bill.totalOvertimeHours > 0 && `, OT: ${formatHours(bill.totalOvertimeHours)}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(bill.grossAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(bill.netAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(bill.status)}
                      color={getStatusColor(bill.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(bill.generatedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewBill(bill)}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDownloadBillPdf(bill)}
                        title="Download PDF"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEmailDialog(bill)}
                        title="Email Invoice"
                      >
                        <EmailIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRows.includes(bill.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Bill Summary
                        </Typography>
                        <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Bill Date
                            </Typography>
                            <Typography variant="body2">
                              {format(new Date(bill.billDate), 'MMM dd, yyyy')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Period
                            </Typography>
                            <Typography variant="body2">
                              {format(new Date(bill.periodStart), 'MMM dd')} - {format(new Date(bill.periodEnd), 'MMM dd, yyyy')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total Shifts
                            </Typography>
                            <Typography variant="body2">
                              {bill.totalShifts}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Regular Amount
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(bill.regularAmount)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Overtime Amount
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(bill.overtimeAmount)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total Deductions
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(bill.totalDeductions)}
                            </Typography>
                          </Box>
                        </Box>
                        {bill.notes && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Notes
                            </Typography>
                            <Typography variant="body2">
                              {bill.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bill Detail Dialog */}
      <Dialog 
        open={!!selectedBill} 
        onClose={handleCloseBillDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bill Details - {selectedBill?.billNumber}
        </DialogTitle>
        <DialogContent>
          {selectedBill && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedBill.staffName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Employee ID: {selectedBill.employeeId}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" gap={4} flexWrap="wrap" mb={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1">
                    {selectedBill.billNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bill Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedBill.billDate), 'MMM dd, yyyy')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={getStatusText(selectedBill.status)}
                      color={getStatusColor(selectedBill.status)}
                      size="small"
                    />
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Box display="flex" gap={4} flexWrap="wrap" mb={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Hours
                  </Typography>
                  <Typography variant="body1">
                    {formatHours(selectedBill.totalHours)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Regular Hours
                  </Typography>
                  <Typography variant="body1">
                    {formatHours(selectedBill.totalRegularHours)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Overtime Hours
                  </Typography>
                  <Typography variant="body1">
                    {formatHours(selectedBill.totalOvertimeHours)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Gross Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(selectedBill.grossAmount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Deductions
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(selectedBill.totalDeductions)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Net Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(selectedBill.netAmount)}
                  </Typography>
                </Box>
              </Box>

              {selectedBill.notes && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedBill.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBillDialog}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />}
            onClick={() => selectedBill && handleDownloadBillPdf(selectedBill)}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={!!emailBill} onClose={() => !emailSending && setEmailBill(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Invoice to Employee</DialogTitle>
        <DialogContent>
          {emailBill && (
            <Box pt={1} display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Invoice <strong>{emailBill.billNumber}</strong> for <strong>{emailBill.staffName}</strong>
              </Typography>
              {emailError && <Alert severity="error">{emailError}</Alert>}
              <TextField
                label="Recipient Email"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                fullWidth
                autoFocus
                placeholder="employee@example.com"
              />
              <Typography variant="caption" color="text.secondary">
                The invoice PDF will be generated and attached to the email automatically.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailBill(null)} disabled={emailSending}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" startIcon={<EmailIcon />} disabled={emailSending}>
            {emailSending ? <CircularProgress size={20} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!snackMessage}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
      />
    </Box>
  );
};

export default BillsView;